import { useEffect, useState } from 'react';
import { Check, Mail } from 'lucide-react';
import {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalPanel,
} from '../Common/ModalShell';
import {
  NEWSLETTER_SENDER_FROM,
  NEWSLETTER_SENDER_REPLY_TO,
} from '../../lib/newsletterSender';
import { SenderEnvelope } from './SenderEnvelope';
import type { PostmarkIdentity } from './types';

/**
 * Choose the From address for a send. Standalone so anything with a sender — an
 * automation, a newsletter, a one-off blast — opens the same dialog rather than each
 * screen growing its own list.
 *
 * Takes signatures as a prop instead of fetching: the caller already loads them to
 * render its own summary, and a second fetch on open would show a spinner over data
 * that is on screen behind the modal.
 */
export interface PostmarkSignatureModalProps {
  isOpen: boolean;
  /** Signature id in force, or null for the engine default sender. */
  value: number | null;
  signatures: PostmarkIdentity[];
  onClose: () => void;
  onSelect: (signatureId: number | null) => void;
}

export default function PostmarkSignatureModal({
  isOpen,
  value,
  signatures,
  onClose,
  onSelect,
}: PostmarkSignatureModalProps) {
  // Staged, so closing without confirming leaves the sender untouched.
  const [pending, setPending] = useState<number | null>(value);

  useEffect(() => {
    if (isOpen) setPending(value);
  }, [isOpen, value]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // No backdrop close — a stray click outside must not discard a staged sender.
  return (
    <ModalOverlay onClose={onClose}>
      <ModalPanel maxWidthClass="max-w-md" titleId="postmark-signature-modal-title">
        <ModalHeader
          title="Postmark signature"
          titleId="postmark-signature-modal-title"
          icon={<Mail className="w-5 h-5 text-emerald-500" />}
          onClose={onClose}
        />
        {/* Capped short on desktop rather than growing with the list — the panel is
            sm:h-auto, so this is what keeps a long sender list scrolling in place. */}
        <ModalBody className="space-y-2 sm:max-h-[50vh]">
          <SignatureOption
            active={pending === null}
            title="Default (engine)"
            from={NEWSLETTER_SENDER_FROM}
            replyTo={NEWSLETTER_SENDER_REPLY_TO}
            onSelect={() => setPending(null)}
          />
          {signatures.map((sig) => (
            <SignatureOption
              key={sig.id}
              active={pending === sig.id}
              title={`${sig.name || sig.emailAddress || 'Signature'} · id ${sig.id}`}
              from={sig.fromLine}
              replyTo={sig.replyToEmailAddress ?? 'not set'}
              unconfirmed={!sig.confirmed}
              onSelect={() => setPending(sig.id)}
            />
          ))}
          {signatures.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
              No signatures in Postmark yet. Add a signature row per From address —
              verified domains alone are not selectable.
            </p>
          ) : null}
        </ModalBody>
        <ModalFooter className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              onSelect(pending);
              onClose();
            }}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-600 rounded-lg transition-colors"
          >
            <Check className="w-4 h-4" />
            Use this sender
          </button>
        </ModalFooter>
      </ModalPanel>
    </ModalOverlay>
  );
}

function SignatureOption({
  active,
  title,
  from,
  replyTo,
  unconfirmed = false,
  onSelect,
}: {
  active: boolean;
  title: string;
  from: string;
  replyTo: string;
  unconfirmed?: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      className={`w-full text-left rounded-lg border px-3 py-2.5 transition-colors ${
        active
          ? 'border-emerald-500 bg-emerald-50/60 dark:bg-emerald-900/20 dark:border-emerald-600'
          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-900/30'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
          {title}
        </p>
        <div className="flex items-center gap-1.5 shrink-0">
          {unconfirmed ? (
            <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
              Unconfirmed
            </span>
          ) : null}
          {active ? (
            <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
          ) : null}
        </div>
      </div>
      <SenderEnvelope from={from} replyTo={replyTo} />
    </button>
  );
}
